import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { ShieldAlert, Copy, ExternalLink, RefreshCcw } from "lucide-react";

const STATUS = {
  open: { label: "Open", cls: "bg-[#EF4444]/15 text-[#EF4444]" },
  reported_discord: { label: "Reported to Discord", cls: "bg-[#8B5CF6]/15 text-[#A78BFA]" },
  reported_police: { label: "Reported to police", cls: "bg-[#7DD3FC]/15 text-[#7DD3FC]" },
  reported_both: { label: "Reported to both", cls: "bg-[#10B981]/15 text-[#10B981]" },
  closed_false_positive: { label: "False positive", cls: "bg-white/5 text-[#94A3B8]" },
  closed: { label: "Closed", cls: "bg-white/5 text-[#94A3B8]" },
};

const CaseCard = ({ c, links, onUpdate }) => {
  const [notes, setNotes] = useState(c.notes || "");
  const copy = async () => {
    try { await navigator.clipboard.writeText(c.summary); toast.success("Case summary copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="bg-[#0D0E15] border border-white/10 p-5 space-y-4" data-testid={`cs-case-${c.case_number}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-display font-bold text-lg">Case #{c.case_number}</span>
        <span data-testid={`cs-case-${c.case_number}-status`} className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${STATUS[c.status]?.cls}`}>{STATUS[c.status]?.label}</span>
        <span className="ml-auto font-mono text-[11px] text-[#64748B]">{new Date(c.created_at).toLocaleString()}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <div><span className="text-[#64748B]">Server:</span> {c.guild_name || "?"} <span className="font-mono text-[#64748B]">{c.guild_id}</span></div>
        <div><span className="text-[#64748B]">User:</span> {c.author_name} <span className="font-mono text-[#64748B]">{c.author_id}</span></div>
        <div><span className="text-[#64748B]">Message ID:</span> <span className="font-mono">{c.message_id}</span></div>
        <div><span className="text-[#64748B]">Actions:</span> {(c.actions || []).join(", ") || "none"}</div>
      </div>
      <div className="bg-[#141622] border border-white/5 p-3 text-sm text-[#E2E8F0] whitespace-pre-wrap break-words" data-testid={`cs-case-${c.case_number}-text`}>{c.content}</div>
      {c.reason && <div className="text-xs text-[#94A3B8]">Detector: {c.reason}</div>}
      <div className="flex flex-wrap gap-2">
        <button data-testid={`cs-copy-${c.case_number}`} onClick={copy} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-medium"><Copy className="w-3.5 h-3.5" /> Copy case summary</button>
        <a href={links.discord} target="_blank" rel="noopener noreferrer" data-testid={`cs-discord-${c.case_number}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-white/10 hover:border-[#8B5CF6] text-xs">Report to Discord <ExternalLink className="w-3 h-3" /></a>
        <a href={links.ncmec} target="_blank" rel="noopener noreferrer" data-testid={`cs-ncmec-${c.case_number}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-white/10 hover:border-[#7DD3FC] text-xs">Report to NCMEC / police <ExternalLink className="w-3 h-3" /></a>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid={`cs-status-${c.case_number}`} value={c.status} onChange={e => onUpdate(c.id, { status: e.target.value })} className="px-2 py-1.5 text-xs border">
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input data-testid={`cs-notes-${c.case_number}`} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (report numbers, police ref…)" className="flex-1 min-w-[200px] px-3 py-1.5 text-xs border" />
        <button data-testid={`cs-save-notes-${c.case_number}`} onClick={() => onUpdate(c.id, { notes })} className="px-3 py-1.5 text-xs border border-white/10 hover:border-[#10B981] hover:text-[#10B981]">Save notes</button>
      </div>
    </div>
  );
};

export const ChildSafety = () => {
  const [data, setData] = useState(null);
  const load = () => api.get("/owner/child-safety").then(r => setData(r.data)).catch(() => toast.error("Failed to load cases"));
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => {
    try { await api.patch(`/owner/child-safety/${id}`, patch); toast.success("Case updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  return (
    <div className="space-y-4" data-testid="child-safety">
      <div className="bg-[#0D0E15] border border-[#EF4444]/25 p-5 flex flex-wrap items-start gap-4">
        <ShieldAlert className="w-6 h-6 text-[#EF4444] shrink-0" />
        <div className="flex-1 min-w-[240px] text-sm text-[#94A3B8] space-y-1">
          <div className="text-white font-medium">Grooming detection · {data ? `${data.open} open` : "…"}</div>
          <div>When the AI moderator detects grooming of a minor, ModNode bans the user, deletes the message and opens a case here. Report each case to Discord and to NCMEC (US) or your local police, then update its status.</div>
          <div className="text-xs text-[#64748B]">Only text is stored. Never download, screenshot or forward images — report the IDs and let Discord and the police collect them.</div>
        </div>
        <button data-testid="cs-refresh" onClick={load} className="p-2 border border-white/10 hover:border-[#8B5CF6] text-[#94A3B8]"><RefreshCcw className="w-4 h-4" /></button>
      </div>
      {data && data.items.length === 0 && <div className="font-mono text-xs text-[#64748B]" data-testid="cs-empty">// No child-safety cases. Good.</div>}
      {data?.items.map(c => <CaseCard key={c.id} c={c} links={data.links} onUpdate={update} />)}
    </div>
  );
};
